import { useOverlayTriggerState } from '@react-stately/overlays'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Button } from 'blaze-ui/Button'
import { ButtonVariant, ButtonSize } from 'blaze-ui/Button/Types'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { CalendarTrash, ChevronDown, Send1 } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import dayjs from 'dayjs'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { DesignActionType, ScheduledPostTarget } from 'types'
import { useCompleteDesignAction } from 'components/Design/utils/useCompleteDesignAction'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import Styles from './Toolbar.module.scss'
import { ConfirmModal } from 'blaze-components/ConfirmModal/ConfirmModal'
import { useModals } from 'almanac-ui/Modal'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { usePublishScheduledPost } from 'hooks/queries/scheduledPosts/usePublishScheduledPost'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { providerFromChannelType, supportedBlogProviders } from 'components/ArticlePostPreview/utils'
import { SOCIAL_CROSSPOST_CHANNELS, SOCIAL_VIDEO_PLATFORMS } from '../utils'
import cx from 'classnames'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import { CalendarFilters } from 'screens/Calendar/types'
import { useQueryClient } from 'react-query'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/useScheduledPostTargets'

const ACTIVE_STATUSES: Array<ScheduledPostTarget['status']> = ['queued', 'processing', 'retrying']
interface PostingOnMenuProps {
  almanacFile: AlmanacFileShowItem
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onDelete: () => void
  filters: CalendarFilters
  fileChannel: ChannelType | DesignChannel | GenerationType
  startDate?: string
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  buttonFullWidth?: boolean
  buttonClassName?: string
  trigger?: ReactNode
  reviewAndSchedule?: boolean
}

export const PostingOnMenu = ({
  almanacFile,
  isLoading,
  setIsLoading,
  onDelete,
  filters,
  fileChannel,
  startDate,
  onUpdateAlmanacFile,
  buttonVariant = 'tertiary',
  buttonSize = 'lg',
  buttonFullWidth = true,
  buttonClassName,
  trigger,
  reviewAndSchedule = false,
}: PostingOnMenuProps) => {
  const completeActionMutation = useCompleteDesignAction()
  const { openModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { mutateAsync: publishScheduledPost } = usePublishScheduledPost()
  const { data: integrationsData } = useExternalIntegrations()
  const wasPublishing = useRef(false)
  const failedTargetIds = almanacFile.scheduled_post_targets
    ?.filter((target) => target.status === 'failed')
    .map((target) => target.id)
  const hasFailedTargets = failedTargetIds.length > 0

  const { data: targets } = useScheduledPostTargets(almanacFile.scheduled_post?.id ?? 0, {
    enablePolling: true,
    enabled: !!almanacFile.scheduled_post?.id,
  })

  const { mutate: publishScheduledPostTargets } = usePublishScheduledPostTargets({
    onSuccess: () => {
      if (hasFailedTargets) {
        queryClient.invalidateQueries(filesKeys.partialRecentsInfinite(workspaceId))
      }
    },
  })

  useEffect(() => {
    if (!targets) return

    const isPublishing = targets.some((t) => ACTIVE_STATUSES.includes(t.status))

    if (wasPublishing.current && !isPublishing) {
      queryClient.invalidateQueries(filesKeys.detail(workspaceId, almanacFile.key))
    }

    wasPublishing.current = isPublishing
  }, [targets, queryClient, workspaceId, almanacFile.key])

  const updateFileMutation = useUpdateFile(almanacFile.key, {
    onSuccess: (updatedFile) => {
      if (onUpdateAlmanacFile) {
        onUpdateAlmanacFile(updatedFile as AlmanacFileShowItem)
      }
      queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
    },
  })

  const scheduledPostProvider = almanacFile.scheduled_post?.external_integration?.provider as ExternalIntegrationEnum
  const isCrossPostSupported = SOCIAL_CROSSPOST_CHANNELS.includes(almanacFile.channel as DesignChannel)
  const isVideoCrossPostSupported = scheduledPostProvider && SOCIAL_VIDEO_PLATFORMS.includes(scheduledPostProvider)

  const handlePublish = () => {
    const scheduledPostId = almanacFile.scheduled_post?.id
    if (!scheduledPostId) return

    if (isCrossPostSupported || isVideoCrossPostSupported) {
      publishScheduledPostTargets({
        scheduledPostId,
        targetIds: hasFailedTargets ? failedTargetIds : undefined,
      })
    } else {
      publishScheduledPost({ id: scheduledPostId })
    }
  }

  const overlayState = useOverlayTriggerState({})
  const [calendarPickerPostAt, setCalendarPickerPostAt] = useState<string | null>(startDate || null)

  const handleUnschedule = async (close: () => void) => {
    setIsLoading(true)

    try {
      // "Deleting" means removing post_at and setting status to Draft
      await updateFileMutation.mutateAsync({
        post_at: null,
        post_status: PostStatusPropertyValue.Draft,
      })
      showToastMessage(`Post Unscheduled. Go to Recents to reschedule`, 'success')
      onDelete()
    } catch (error) {
      showToastMessage(`Failed to unschedule post`, 'error')
    } finally {
      setIsLoading(false)
      close()
    }
  }

  const handleSchedule = async (date: Date, status: PostStatusPropertyValue) => {
    if (!almanacFile) return

    setIsLoading(true)

    try {
      const formattedDate = dayjs(date).format('ddd, MMM DD, h:mm A')

      // Update the file with new post_at and status
      await updateFileMutation.mutateAsync({
        post_at: date.toISOString(),
        post_status: status,
        reviewed_at: reviewAndSchedule ? dayjs().toISOString() : null,
      })

      const isScheduled = almanacFile.post_status === PostStatusPropertyValue.AutoPostScheduled
      showToastMessage(
        isScheduled ? `Post Updated For ${formattedDate}` : `Post Scheduled For ${formattedDate}`,
        'success',
      )

      if (almanacFile.asset_type === 'Design') {
        completeActionMutation.mutate({ action: DesignActionType.SchedulePost })
      }
    } catch (error) {
      showToastMessage(`Failed to schedule post`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const confirmPublish = () => {
    openModal(
      ConfirmModal,
      {
        title: 'Publish Post',
        cancelText: 'Cancel',
        confirmText: 'Publish',
        prompt: 'Are you sure you want to publish this post to the selected platforms?',
        onConfirm: handlePublish,
      },
      { newStack: true },
    )
  }

  const scheduledPostTargets = almanacFile.scheduled_post_targets || []
  let canPostNow: boolean | undefined
  if (isCrossPostSupported) {
    canPostNow = scheduledPostTargets.length > 0
  } else if (fileChannel === ChannelType.BlogPost) {
    canPostNow = !!integrationsData?.external_integrations.find((integration) =>
      supportedBlogProviders.includes(integration.provider),
    )
  } else {
    canPostNow = !!integrationsData?.external_integrations.find(
      (integration) => integration.provider === providerFromChannelType[fileChannel as SocialChannelType],
    )
  }

  const tooltipLabel = isCrossPostSupported
    ? 'You must enable at least one channel to post'
    : 'Connect your account to post on this platform'

  const buttonWrapperClassName = cx(Styles.CalendarButtonWrapper, {
    [Styles.CalendarButtonWrapperCompact]: !buttonFullWidth,
  })
  const triggerButtonClassName = cx(buttonClassName, {
    [Styles.CalendarButton]: buttonFullWidth,
  })

  const Trigger = trigger ? (
    trigger
  ) : (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      endIcon={ChevronDown}
      isDisabled={isLoading}
      fullWidth={buttonFullWidth}
      className={triggerButtonClassName}
    >
      {almanacFile?.post_at ? dayjs(almanacFile.post_at).format('ddd, MMM D h:mma') : 'Schedule Post'}
    </Button>
  )

  return (
    <div className={buttonWrapperClassName}>
      <DateTimePicker
        onClose={overlayState.close}
        dateTime={calendarPickerPostAt ? dayjs(calendarPickerPostAt) : undefined}
        actions={(close) => (
          <div className={Styles.actions}>
            <Tooltip label={tooltipLabel} hide={!!canPostNow}>
              <div>
                <Button
                  size="md"
                  fullWidth
                  onPress={() => {
                    if (!calendarPickerPostAt) return

                    const status = PostStatusPropertyValue.AutoPostScheduled
                    handleSchedule(new Date(calendarPickerPostAt), status)
                    close()
                  }}
                  isDisabled={isLoading}
                >
                  {almanacFile?.post_at ? 'Update Date' : 'Schedule Post'}
                </Button>
              </div>
            </Tooltip>
            <div className={Styles.bottomActions}>
              {almanacFile?.post_at && almanacFile.post_status !== PostStatusPropertyValue.Posted && (
                <Button
                  variant="tertiary"
                  frontIcon={CalendarTrash}
                  fullWidth
                  size="md"
                  isDisabled={isLoading}
                  onPress={() => {
                    handleUnschedule(close)
                  }}
                >
                  {almanacFile.post_status === PostStatusPropertyValue.AutoPostScheduled ? 'Unschedule' : 'Remove date'}
                </Button>
              )}
              {almanacFile.post_status !== PostStatusPropertyValue.Posted && (
                <Tooltip label={tooltipLabel} hide={!!canPostNow}>
                  <div>
                    <Button
                      variant="tertiary"
                      frontIcon={Send1}
                      fullWidth
                      size="md"
                      isDisabled={isLoading || !canPostNow}
                      onPress={() => {
                        close()
                        confirmPublish()
                      }}
                    >
                      {failedTargetIds.length > 0 ? 'Retry Failed' : 'Post Now'}
                    </Button>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        )}
        setDateTime={(datetime) => {
          if (datetime) setCalendarPickerPostAt(datetime.toISOString())
        }}
      >
        {Trigger}
      </DateTimePicker>
    </div>
  )
}
